import { z } from "zod";
import {
  contactVerificationSchema,
  influenceScopeSchema,
  labContactRelationshipStatusSchema,
  labContactRoleSchema,
} from "./contact.js";
import {
  captureOutcomeSchema,
  communicationDirectionSchema,
  interactionTypeSchema,
} from "./interaction.js";
import {
  contactLabScopeTargetInputShape,
  validateContactLabScopeTargetInput,
} from "./territory-relationship.js";
import {
  confidenceSchema,
  observationSourceSchema,
  quantityQualifierSchema,
  quantityScopeSchema,
  timingObservationPayloadSchema,
} from "./observation.js";
import { accreditationRequirementSchema } from "./service.js";
import { calendarDateSchema, isoTimestampSchema } from "./timing.js";

const nonBlankString = (max: number) =>
  z
    .string()
    .max(max)
    .refine((value) => value.trim().length > 0, {
      message: "Value must contain a non-whitespace character",
    });

export const duplicateContactDecisionSchema = z.enum([
  "create_if_no_match",
  "create_anyway",
]);
export type DuplicateContactDecision = z.infer<
  typeof duplicateContactDecisionSchema
>;

const contactReferenceFieldsSchema = z
  .object({
    contactId: z.uuid().nullable().optional(),
    name: nonBlankString(200).optional(),
    email: z.string().max(320).nullable().optional(),
    phone: z.string().max(100).nullable().optional(),
    duplicateDecision: duplicateContactDecisionSchema.optional(),
  })
  .strict();

type ContactReferenceFields = z.infer<typeof contactReferenceFieldsSchema>;

function validateContactReference(
  value: ContactReferenceFields,
  context: z.RefinementCtx,
): void {
  if (value.contactId === undefined || value.contactId === null) {
    if (value.name === undefined) {
      context.addIssue({
        code: "custom",
        path: ["name"],
        message: "A name is required when contactId is not supplied",
      });
    }
    if (value.duplicateDecision === undefined) {
      context.addIssue({
        code: "custom",
        path: ["duplicateDecision"],
        message:
          "An explicit duplicate decision is required for inline Contact creation",
      });
    }
    return;
  }

  for (const field of [
    "name",
    "email",
    "phone",
    "duplicateDecision",
  ] as const) {
    if (value[field] !== undefined && value[field] !== null) {
      context.addIssue({
        code: "custom",
        path: [field],
        message: `${field} is not accepted when contactId is supplied`,
      });
    }
  }
}

const contactReferenceSchema = contactReferenceFieldsSchema.superRefine(
  validateContactReference,
);

export const quickInteractionContextSchema = z
  .object({
    labId: z.uuid().nullable().optional(),
    locationId: z.uuid().nullable().optional(),
  })
  .strict()
  .refine((value) => value.labId != null || value.locationId != null, {
    message: "At least one of labId or locationId is required",
  });

const factMetadataSchema = z
  .object({
    source: observationSourceSchema.optional(),
    confidence: confidenceSchema.optional(),
  })
  .strict();

const quantityCaptureSchema = z
  .object({
    scope: quantityScopeSchema.default("current_installed_base"),
    value: z.number().int().nonnegative(),
    qualifier: quantityQualifierSchema,
    effectiveDate: calendarDateSchema.optional(),
  })
  .merge(factMetadataSchema);

const timingCaptureSchema =
  timingObservationPayloadSchema.and(factMetadataSchema);

const providerCaptureSchema = z
  .object({
    name: nonBlankString(200),
  })
  .merge(factMetadataSchema);

const accreditationCaptureSchema = z
  .object({
    requirement: accreditationRequirementSchema,
  })
  .merge(factMetadataSchema);

const categoryCaptureSchema = z
  .object({
    quantity: quantityCaptureSchema.optional(),
    timing: timingCaptureSchema.optional(),
    provider: providerCaptureSchema.optional(),
    accreditationRequirement: accreditationCaptureSchema.optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.quantity !== undefined ||
      value.timing !== undefined ||
      value.provider !== undefined ||
      value.accreditationRequirement !== undefined,
    { message: "A category must contain at least one service fact" },
  );

const responsibilityCaptureSchema = z
  .object({
    role: labContactRoleSchema.nullable().default(null),
    calibrationResponsible: z.boolean().nullable().default(null),
    ...contactLabScopeTargetInputShape,
    scopeNote: z.string().max(1000).optional(),
    relationshipStatus: labContactRelationshipStatusSchema.optional(),
    source: observationSourceSchema.optional(),
    confidence: confidenceSchema.optional(),
  })
  .strict()
  .superRefine(validateContactLabScopeTargetInput);

const identityCaptureSchema = z
  .object({
    verificationStatus: z.literal(contactVerificationSchema.enum.verified),
    source: observationSourceSchema.optional(),
    confidence: confidenceSchema.optional(),
  })
  .strict();

const referralCaptureSchema = contactReferenceFieldsSchema
  .extend({
    suggestedRole: labContactRoleSchema.optional(),
    influenceScope: influenceScopeSchema.optional(),
    scopeNote: z.string().max(1000).optional(),
    source: observationSourceSchema.optional(),
    confidence: confidenceSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    validateContactReference(value, context);
  });

export const quickInteractionCaptureSchema = z
  .object({
    clientRequestId: z.string().trim().min(1).max(200),
    context: quickInteractionContextSchema,
    occurredAt: isoTimestampSchema,
    interactionType: interactionTypeSchema.default("field_visit"),
    communicationDirection: communicationDirectionSchema.default("unknown"),
    captureOutcome: captureOutcomeSchema.default("information_captured"),
    primaryContact: contactReferenceSchema.nullable().optional(),
    contactResponsibility: responsibilityCaptureSchema.optional(),
    contactIdentity: identityCaptureSchema.optional(),
    pipettes: categoryCaptureSchema.nullable().optional(),
    balances: categoryCaptureSchema.nullable().optional(),
    generalCalibrationContact: referralCaptureSchema.nullable().optional(),
    note: z.string().max(5000).default(""),
  })
  .strict();

export type QuickInteractionCapture = z.infer<
  typeof quickInteractionCaptureSchema
>;

export type QuickInteractionCategoryCapture = z.infer<
  typeof categoryCaptureSchema
>;

export type QuickInteractionContactReference = z.infer<
  typeof contactReferenceSchema
>;

export type QuickInteractionReferralCapture = z.infer<
  typeof referralCaptureSchema
>;

export type QuickInteractionFactMetadata = z.infer<typeof factMetadataSchema>;
